import React, { useMemo, useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';

const LedScreenProperties = ({ selectedObject, updateSceneObject, onOpenContentBrowser }) => {
  const led = selectedObject?.ledConfig;
  const [curveAngleText, setCurveAngleText] = useState(String(led?.curveAngle ?? 0));
  const [cabinetStepText, setCabinetStepText] = useState(String(led?.cabinetStep || led?.tileWidth || 500));

  // Sync local text when the actual value changes externally (undo, different object selected)
  useEffect(() => {
    setCurveAngleText(String(led?.curveAngle ?? 0));
    setCabinetStepText(String(led?.cabinetStep || led?.tileWidth || 500));
  }, [led?.curveAngle, led?.cabinetStep, led?.tileWidth, selectedObject?.id]);

  const calc = useMemo(() => {
    if (!led) return null;
    const cols = led.columns || 1;
    const rows = led.rows || 1;
    const tileW = led.tileWidth || 500;
    const tileH = led.tileHeight || 500;
    const tileResHPx = led.tileResH || 128;
    const tileResVPx = led.tileResV || 128;
    const ledMode = led.ledMode || 'standard';
    const modeIsCorner = ledMode === 'corner';
    const modeIsSoftCorner = ledMode === 'soft_corner';

    // Compute effective layout based on mode
    let effectiveCols, totalW, cornerPixelsH = 0, totalTiles;
    if (modeIsCorner) {
      const leftCols = Math.max(1, led.leftColumns || Math.ceil(cols / 2));
      const rightCols = Math.max(1, led.rightColumns || Math.floor(cols / 2));
      effectiveCols = leftCols + rightCols;
      totalW = effectiveCols * tileW; // unfolded flat
      totalTiles = effectiveCols * rows;
    } else if (modeIsSoftCorner) {
      const leftCols = Math.max(1, led.leftColumns || Math.ceil(cols / 2));
      const rightCols = Math.max(1, led.rightColumns || Math.floor(cols / 2));
      effectiveCols = leftCols + rightCols;
      totalW = leftCols * tileW + 125 + rightCols * tileW; // left + 125mm arc + right
      cornerPixelsH = Math.round(125 / tileW * tileResHPx);
      totalTiles = effectiveCols * rows; // flat tiles
    } else {
      effectiveCols = cols;
      totalW = tileW * cols;
      totalTiles = cols * rows;
      // Add soft corner ribbon widths if enabled on standard wall
      if (led.softCornerLeft) { totalW += 125; cornerPixelsH += Math.round(125 / tileW * tileResHPx); }
      if (led.softCornerRight) { totalW += 125; cornerPixelsH += Math.round(125 / tileW * tileResHPx); }
    }

    const totalH = tileH * rows;
    const totalResH = tileResHPx * effectiveCols + cornerPixelsH;
    const totalResV = tileResVPx * rows;
    const totalPower = (led.powerPerTile || 200) * totalTiles;
    const removedCount = (led.removedTiles || []).length;
    const activeTiles = totalTiles - removedCount;
    const activePower = (led.powerPerTile || 200) * activeTiles;
    const pixelDensity = led.pixelPitch ? (1000 / led.pixelPitch).toFixed(1) : '-';
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const d = gcd(Math.round(totalW), Math.round(totalH));
    const aspectRatio = d > 0 ? `${Math.round(totalW / d)}:${Math.round(totalH / d)}` : '-';

    const curveAngle = led.curveAngle || 0;
    const cabinetStepMm = led.cabinetStep || tileW;
    const numCabinets = Math.max(1, Math.round((tileW * cols) / cabinetStepMm));
    const totalCurveAngle = Math.abs(curveAngle) * numCabinets;
    const curveAngleRad = Math.abs(curveAngle) * (Math.PI / 180);
    const cabinetStepM = cabinetStepMm / 1000;
    const tileD = (led.tileDepth || 80) / 1000;

    let radius = 0, arcLength = 0, innerRadius = 0, outerRadius = 0;
    if (curveAngle !== 0 && curveAngleRad > 0.0001) {
      radius = cabinetStepM / curveAngleRad;
      const totalAngleRad = totalCurveAngle * (Math.PI / 180);
      arcLength = radius * totalAngleRad;
      innerRadius = radius - tileD / 2;
      outerRadius = radius + tileD / 2;
    }

    return {
      cols: effectiveCols, rows, totalTiles, activeTiles, removedCount, totalW, totalH, totalResH, totalResV,
      totalPower, activePower, pixelDensity, aspectRatio,
      numCabinets, totalCurveAngle, radius, arcLength, innerRadius, outerRadius,
      modeIsCorner, modeIsSoftCorner,
      leftColCount: modeIsCorner || modeIsSoftCorner ? Math.max(1, led.leftColumns || Math.ceil(cols / 2)) : 0,
      rightColCount: modeIsCorner || modeIsSoftCorner ? Math.max(1, led.rightColumns || Math.floor(cols / 2)) : 0
    };
  }, [led]);

  if (!led || !calc) return null;

  const update = (field, value) => {
    updateSceneObject(selectedObject.id, {
      ledConfig: { ...led, [field]: value }
    });
  };

  const ledMode = led.ledMode || 'standard';
  const isCorner = ledMode === 'corner';
  const isSoftCorner = ledMode === 'soft_corner';
  const isFlexible = ledMode === 'flexible';
  const isCurved = ledMode === 'curved' || (led.curveAngle || 0) !== 0;

  return (
    <>
      {/* Tile Configuration */}
      <div className="space-y-3">
        <div className="properties-section-title">LED Tile Configuration</div>

        {/* Tile Size */}
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Tile Size (mm)</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Width</Label>
              <Input
                type="number" min={50} step={50}
                value={led.tileWidth || 500}
                onChange={(e) => update('tileWidth', parseFloat(e.target.value) || 500)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-tile-width"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Height</Label>
              <Input
                type="number" min={50} step={50}
                value={led.tileHeight || 500}
                onChange={(e) => update('tileHeight', parseFloat(e.target.value) || 500)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-tile-height"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Depth</Label>
              <Input
                type="number" min={10} step={10}
                value={led.tileDepth || 80}
                onChange={(e) => update('tileDepth', parseFloat(e.target.value) || 80)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-tile-depth"
              />
            </div>
          </div>
        </div>

        {/* Tile Resolution */}
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Tile Resolution (px)</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Horizontal</Label>
              <Input
                type="number" min={1}
                value={led.tileResH || 128}
                onChange={(e) => update('tileResH', parseInt(e.target.value) || 128)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-tile-res-h"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Vertical</Label>
              <Input
                type="number" min={1}
                value={led.tileResV || 128}
                onChange={(e) => update('tileResV', parseInt(e.target.value) || 128)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-tile-res-v"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Pitch (mm)</Label>
              <Input
                type="number" min={0.5} step={0.1}
                value={led.pixelPitch || 3.9}
                onChange={(e) => update('pixelPitch', parseFloat(e.target.value) || 3.9)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-pixel-pitch"
              />
            </div>
          </div>
        </div>

        {/* Tile Quantity */}
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Tile Quantity</div>
          {(isCorner || isSoftCorner) ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-zinc-500">Left Columns</Label>
                  <Input
                    type="number" min={1} max={100}
                    value={led.leftColumns || Math.ceil((led.columns || 4) / 2)}
                    onChange={(e) => update('leftColumns', Math.max(1, parseInt(e.target.value) || 1))}
                    className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                    data-testid="led-corner-left-cols"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Right Columns</Label>
                  <Input
                    type="number" min={1} max={100}
                    value={led.rightColumns || Math.floor((led.columns || 4) / 2)}
                    onChange={(e) => update('rightColumns', Math.max(1, parseInt(e.target.value) || 1))}
                    className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                    data-testid="led-corner-right-cols"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Rows</Label>
                <Input
                  type="number" min={1} max={100}
                  value={led.rows || 3}
                  onChange={(e) => update('rows', Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                  data-testid="led-rows"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-500">Columns</Label>
                <Input
                  type="number" min={1} max={100}
                  value={led.columns || 4}
                  onChange={(e) => update('columns', Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                  data-testid="led-columns"
                />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Rows</Label>
                <Input
                  type="number" min={1} max={100}
                  value={led.rows || 3}
                  onChange={(e) => update('rows', Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                  data-testid="led-rows"
                />
              </div>
            </div>
          )}
        </div>

        {/* Power */}
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Power</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Per Tile (W)</Label>
              <Input
                type="number" min={0}
                value={led.powerPerTile || 200}
                onChange={(e) => update('powerPerTile', parseFloat(e.target.value) || 0)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-power-per-tile"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Max (W)</Label>
              <Input
                type="number" min={0}
                value={led.maxPowerPerTile || 250}
                onChange={(e) => update('maxPowerPerTile', parseFloat(e.target.value) || 250)}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-max-power"
              />
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-zinc-700" />

      {/* Curvature Settings — shown for Standard/Curved (segmented) and Flexible (smooth) */}
      {!isCorner && !isSoftCorner && (
        <>
          <div className="space-y-3">
            <div className="properties-section-title">Curvature Settings</div>

            {/* Cabinet Curve Step — only for standard/curved, NOT flexible */}
            {!isFlexible && (
              <div className="space-y-2">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Cabinet Curve Step (mm)</div>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={cabinetStepText}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setCabinetStepText(raw);
                  }}
                  onBlur={() => {
                    let val = parseInt(cabinetStepText);
                    if (isNaN(val) || val < 10) val = led.cabinetStep || 500;
                    if (val > 5000) val = 5000;
                    if (val < 10) val = 10;
                    setCabinetStepText(String(val));
                    update('cabinetStep', val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      let val = parseInt(cabinetStepText);
                      if (isNaN(val) || val < 10) val = led.cabinetStep || 500;
                      if (val > 5000) val = 5000;
                      if (val < 10) val = 10;
                      setCabinetStepText(String(val));
                      update('cabinetStep', val);
                    }
                  }}
                  className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                  data-testid="led-cabinet-step"
                  placeholder="e.g. 500"
                />
                <div className="flex gap-1">
                  {[250, 500, 1000, 1500].map((v) => (
                    <button
                      key={v}
                      onClick={() => { setCabinetStepText(String(v)); update('cabinetStep', v); }}
                      className={`flex-1 h-5 text-[10px] rounded border transition-colors ${
                        (led.cabinetStep || 500) === v
                          ? 'bg-zinc-600 border-zinc-500 text-zinc-100'
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
                      }`}
                      data-testid={`led-step-preset-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                Curve Angle per Cabinet ({'\u00B0'})
              </div>
              <Input
                type="text"
                inputMode="decimal"
                value={curveAngleText}
                onChange={(e) => {
                  const raw = e.target.value;
                  setCurveAngleText(raw);
                  const val = parseFloat(raw);
                  if (!isNaN(val) && val >= -90 && val <= 90) update('curveAngle', val);
                }}
                onBlur={() => {
                  const val = parseFloat(curveAngleText);
                  if (isNaN(val)) setCurveAngleText(String(led.curveAngle ?? 0));
                }}
                className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                data-testid="led-curve-angle"
              />
              <div className="text-[10px] text-zinc-500">
                + Convex &nbsp;|&nbsp; - Concave &nbsp;|&nbsp; 0 = Flat
              </div>
            </div>

            {isFlexible && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-zinc-300">Smooth Bend</div>
                  <div className="text-[10px] text-zinc-500">Continuous curve, no segments</div>
                </div>
                <Switch
                  checked={led.smoothBend !== false}
                  onCheckedChange={(val) => update('smoothBend', val)}
                  data-testid="led-smooth-bend"
                />
              </div>
            )}
          </div>
          <Separator className="bg-zinc-700" />
        </>
      )}

      {/* Corner Tile Settings */}
      {isCorner && (
        <>
          <div className="space-y-3">
            <div className="properties-section-title">Corner Settings</div>
            <div className="space-y-2">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Corner Angle</div>
              <div className="grid grid-cols-3 gap-1">
                {[90, 45, 0].map(angle => (
                  <Button
                    key={angle}
                    variant={(led.cornerAngle || 90) === angle ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => update('cornerAngle', angle)}
                    data-testid={`led-corner-${angle}`}
                  >
                    {angle === 0 ? 'Custom' : `${angle}\u00B0`}
                  </Button>
                ))}
              </div>
              {(led.cornerAngle === 0 || (led.cornerAngle !== 90 && led.cornerAngle !== 45)) && (
                <div>
                  <Label className="text-[10px] text-zinc-500">Custom Angle ({'\u00B0'})</Label>
                  <Input
                    type="number" min={-180} max={180}
                    value={led.cornerAngle || 90}
                    onChange={(e) => update('cornerAngle', parseFloat(e.target.value) || 90)}
                    className="h-7 text-xs bg-zinc-800/50 border-zinc-700"
                    data-testid="led-corner-custom"
                  />
                </div>
              )}
              <div className="text-[10px] text-zinc-500 mt-1">
                Auto gap: {(Math.abs(led.cornerAngle || 90) / 90 * 0.2).toFixed(1)} mm
              </div>
            </div>
          </div>
          <Separator className="bg-zinc-700" />
        </>
      )}

      {/* 90° Soft Corner Module — REMOVED per user request (Feb 2026).
          Block kept hidden; logic in BabylonScene.js still respects
          softCornerLeft/Right flags on existing saved scenes. */}
      {false && !isCorner && !isFlexible && !isSoftCorner && (
        <>
          <div className="space-y-3">
            <div className="properties-section-title">90° Soft Corner</div>
            <div className="text-[10px] text-zinc-500">Smooth 90° corner transition (125mm x 500mm module)</div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-300">Left Corner</div>
              <Switch
                checked={!!led.softCornerLeft}
                onCheckedChange={(val) => update('softCornerLeft', val)}
                data-testid="led-soft-corner-left"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-300">Right Corner</div>
              <Switch
                checked={!!led.softCornerRight}
                onCheckedChange={(val) => update('softCornerRight', val)}
                data-testid="led-soft-corner-right"
              />
            </div>
          </div>
          <Separator className="bg-zinc-700" />
        </>
      )}

      {/* Content */}
      <div className="space-y-3">
        <div className="properties-section-title">Content</div>
        <Button
          variant="outline" size="sm"
          onClick={onOpenContentBrowser}
          className="w-full h-7 text-xs border-zinc-700 hover:bg-zinc-700"
          data-testid="led-content-btn"
        >
          {led.contentUrl ? 'Change Content' : 'Assign Content'}
        </Button>
        {led.contentUrl && (
          <Button
            variant="ghost" size="sm"
            onClick={() => updateSceneObject(selectedObject.id, {
              ledConfig: { ...led, contentItemId: null, contentUrl: null }
            })}
            className="w-full h-7 text-xs text-red-400 hover:text-red-300"
            data-testid="led-remove-content-btn"
          >
            Remove Content
          </Button>
        )}
      </div>

      <Separator className="bg-zinc-700" />

      {/* Calculated Values */}
      <div className="space-y-3">
        <div className="properties-section-title">Calculated Values</div>
        <div className="space-y-1.5">
          <CalcRow label="Total Screen Size" value={`${(calc.totalW / 1000).toFixed(2)}m x ${(calc.totalH / 1000).toFixed(2)}m`} testId="led-calc-size" />
          <CalcRow label="Total Resolution" value={`${calc.totalResH} x ${calc.totalResV} px`} testId="led-calc-resolution" />
          <CalcRow label="Total Tiles" value={`${calc.activeTiles}${calc.removedCount > 0 ? ` (${calc.removedCount} removed)` : ''} — ${calc.cols} x ${calc.rows}`} testId="led-calc-tiles" />
          {(calc.modeIsCorner || calc.modeIsSoftCorner) && (
            <>
              <CalcRow label="Left Panel" value={`${calc.leftColCount} x ${calc.rows} = ${calc.leftColCount * calc.rows} tiles (${(calc.leftColCount * led.tileWidth / 1000).toFixed(2)}m)`} testId="led-calc-left-panel" />
              {calc.modeIsSoftCorner && (
                <CalcRow label="Center (Curve)" value={`Ribbon — ${led.cornerAngle || 90}°`} testId="led-calc-center" />
              )}
              <CalcRow label="Right Panel" value={`${calc.rightColCount} x ${calc.rows} = ${calc.rightColCount * calc.rows} tiles (${(calc.rightColCount * led.tileWidth / 1000).toFixed(2)}m)`} testId="led-calc-right-panel" />
            </>
          )}
          <CalcRow label="Pixel Density" value={`${calc.pixelDensity} px/m`} testId="led-calc-density" />
          <CalcRow label="Aspect Ratio" value={calc.aspectRatio} testId="led-calc-aspect" />
          <Separator className="bg-zinc-800 my-1" />
          <div className="flex justify-between text-xs" data-testid="led-calc-power">
            <span className="text-zinc-500">Total Power (Avg)</span>
            <span className="text-amber-400 font-mono font-bold">
              {calc.activePower >= 1000 ? `${(calc.activePower / 1000).toFixed(1)} kW` : `${calc.activePower} W`}
            </span>
          </div>
          {(led.maxPowerPerTile || 0) > 0 && (
            <div className="flex justify-between text-xs" data-testid="led-calc-max-power">
              <span className="text-zinc-500">Total Power (Max)</span>
              <span className="text-amber-500 font-mono">
                {(() => { const m = (led.maxPowerPerTile || 250) * calc.activeTiles; return m >= 1000 ? `${(m / 1000).toFixed(1)} kW` : `${m} W`; })()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Curvature Calculations */}
      {(isCurved || isCorner || isSoftCorner) && (
        <>
          <Separator className="bg-zinc-700" />
          <div className="space-y-3">
            <div className="properties-section-title">{isSoftCorner ? 'Soft Corner Info' : 'Curvature Calculations'}</div>
            <div className="space-y-1.5">
              {isCorner ? (
                <>
                  <CalcRow label="Corner Angle" value={`${led.cornerAngle || 90}\u00B0`} testId="led-calc-corner" />
                  <CalcRow label="Left Wall" value={`${led.leftColumns || Math.ceil((led.columns || 4) / 2)} cols (${((led.leftColumns || Math.ceil((led.columns || 4) / 2)) * (led.tileWidth || 500) / 1000).toFixed(2)}m)`} testId="led-calc-left" />
                  <CalcRow label="Right Wall" value={`${led.rightColumns || Math.floor((led.columns || 4) / 2)} cols (${((led.rightColumns || Math.floor((led.columns || 4) / 2)) * (led.tileWidth || 500) / 1000).toFixed(2)}m)`} testId="led-calc-right" />
                  <CalcRow label="Auto Gap" value={`${(Math.abs(led.cornerAngle || 90) / 90 * 0.2).toFixed(1)} mm`} testId="led-calc-gap" />
                </>
              ) : isSoftCorner ? (
                <>
                  <CalcRow label="Corner Type" value={`90° Soft Curve`} testId="led-calc-sc-type" />
                  <CalcRow label="Corner Arc" value="125mm" testId="led-calc-sc-arc" />
                  <CalcRow label="Arc Radius" value="~79.6mm" testId="led-calc-sc-radius" />
                  <CalcRow label="Left Wall" value={`${led.leftColumns || Math.ceil((led.columns || 4) / 2)} cols (${((led.leftColumns || Math.ceil((led.columns || 4) / 2)) * (led.tileWidth || 500) / 1000).toFixed(2)}m)`} testId="led-calc-sc-left" />
                  <CalcRow label="Right Wall" value={`${led.rightColumns || Math.floor((led.columns || 4) / 2)} cols (${((led.rightColumns || Math.floor((led.columns || 4) / 2)) * (led.tileWidth || 500) / 1000).toFixed(2)}m)`} testId="led-calc-sc-right" />
                </>
              ) : (
                <>
                  <CalcRow label="Total Cabinets" value={`${calc.numCabinets}`} testId="led-calc-cabinets" />
                  <CalcRow label="Total Curve Angle" value={`${calc.totalCurveAngle.toFixed(1)}\u00B0`} testId="led-calc-total-angle" />
                  <CalcRow label="Curve Radius" value={calc.radius > 0 ? `${calc.radius.toFixed(2)}m` : '-'} testId="led-calc-radius" />
                  <CalcRow label="Arc Length" value={calc.arcLength > 0 ? `${calc.arcLength.toFixed(2)}m` : '-'} testId="led-calc-arc" />
                  <Separator className="bg-zinc-800 my-1" />
                  <CalcRow label="Inner Radius" value={calc.innerRadius > 0 ? `${calc.innerRadius.toFixed(2)}m` : '-'} testId="led-calc-inner-r" />
                  <CalcRow label="Outer Radius" value={calc.outerRadius > 0 ? `${calc.outerRadius.toFixed(2)}m` : '-'} testId="led-calc-outer-r" />
                  <CalcRow
                    label="Curve Direction"
                    value={(led.curveAngle || 0) > 0 ? 'Convex' : (led.curveAngle || 0) < 0 ? 'Concave' : 'Flat'}
                    testId="led-calc-direction"
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}

      <Separator className="bg-zinc-700" />

      {/* LED Tile Removal Grid */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] font-medium text-zinc-300">Panel Layout (click to remove/add tiles)</Label>
          {(led.removedTiles || []).length > 0 && (
            <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[9px] text-zinc-400 hover:text-white"
              onClick={() => update('removedTiles', [])} data-testid="reset-tiles-btn">
              Reset All
            </Button>
          )}
        </div>
        <div className="bg-zinc-900 rounded p-2 overflow-x-auto" data-testid="tile-grid-container">
          {/* Panel labels for corner/soft corner */}
          {(calc.modeIsCorner || calc.modeIsSoftCorner) && (
            <div className="flex mb-1 text-[8px] text-zinc-500 font-medium" style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${calc.cols}, 1fr)`,
              gap: '2px'
            }}>
              {Array.from({ length: calc.cols }).map((_, c) => {
                const leftCols = calc.leftColCount || Math.ceil(calc.cols / 2);
                const isLeft = c < leftCols;
                const showLabel = (isLeft && c === 0) || (!isLeft && c === leftCols);
                return (
                  <span key={c} className={`text-center ${c === leftCols ? 'border-l border-zinc-600 pl-0.5' : ''}`}>
                    {showLabel ? (isLeft ? 'L' : 'R') : ''}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${calc.cols}, 1fr)`,
            gap: '2px',
            maxWidth: '100%'
          }}>
            {Array.from({ length: calc.rows }).map((_, r) =>
              Array.from({ length: calc.cols }).map((_, c) => {
                const key = `${r}-${c}`;
                const removed = (led.removedTiles || []).includes(key);
                const viewerLeftCols = calc.leftColCount || Math.ceil(calc.cols / 2);
                const isBorder = (calc.modeIsCorner || calc.modeIsSoftCorner) && c === viewerLeftCols;
                return (
                  <button
                    key={key}
                    data-testid={`tile-${r}-${c}`}
                    onClick={() => {
                      const current = led.removedTiles || [];
                      const next = removed
                        ? current.filter(k => k !== key)
                        : [...current, key];
                      update('removedTiles', next);
                    }}
                    className={`aspect-square rounded-sm text-[7px] font-mono transition-colors min-w-[16px] ${
                      removed
                        ? 'bg-zinc-800 text-zinc-600 border border-dashed border-zinc-700 hover:bg-zinc-700'
                        : 'bg-emerald-600/80 text-white/80 hover:bg-red-500/80 border border-emerald-500/40'
                    } ${isBorder ? 'ml-1' : ''}`}
                    title={removed ? `Add tile [${r+1},${c+1}]` : `Remove tile [${r+1},${c+1}]`}
                  >
                    {calc.cols <= 12 ? `${r+1},${c+1}` : ''}
                  </button>
                );
              })
            )}
          </div>
          {(led.removedTiles || []).length > 0 && (
            <div className="mt-1.5 text-[9px] text-zinc-400">
              {(led.removedTiles || []).length} tile(s) removed | {calc.totalTiles - (led.removedTiles || []).length} active
            </div>
          )}
        </div>
      </div>

      <Separator className="bg-zinc-700" />
    </>
  );
};

const CalcRow = ({ label, value, testId }) => (
  <div className="flex justify-between text-xs" data-testid={testId}>
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-200 font-mono">{value}</span>
  </div>
);

export default LedScreenProperties;
